import api from "api"
import { CartlessOrder } from "api/resources/orders/types"
import { useState } from "react"
import { mapErrorsV2 } from "utils/helpers"

import { usePayment } from "../hooks/usePayment"

type Props = {
  logoSrc: string
  stripeConfig: {
    redirectRoute: string
  }
  razorpayConfig: {
    onSuccess: () => void
    getUserDetails: () => {
      name?: string
      email?: string
      contact?: string
    }
  }
  purchaseDetails: {
    itemName: string
    amount: number
    currency: {
      code: string
      symbol: string
    }
  } | null
}

export const useSubscriptionPayment = (props: Props) => {
  const [stripeForm, setStripeForm] = useState<JSX.Element | null>(null)

  const [order, setOrder] = useState<CartlessOrder | null>(null)

  const paymentPlanId = order?.paymentPlans[0].id

  const { checkout, updateSubscriptionPaymentMethod, changePaymentPlan } =
    usePayment({
      stripeConfig: {
        paymentKey: import.meta.env.VITE_STRIPE_PAYMENT_KEY,
        logoSrc: props.logoSrc,
        getForm: setStripeForm,
        handleClose: () => {
          window.location.reload()
          setStripeForm(null)
        },
        redirectURL: getStripeSuccessRedirectURL(
          props.stripeConfig.redirectRoute
        ),
      },
      razorpayConfig: {
        logo: props.logoSrc,
        successHandler: () => {
          props.razorpayConfig.onSuccess()
        },
        getUserDetails: props.razorpayConfig.getUserDetails,
      },
      purchaseDetails: props.purchaseDetails,
    })

  const createOrder = async ({
    isUserless,
    billingAddressId,
    productSlug,
    promocode,
  }: {
    isUserless: boolean
    billingAddressId: string
    productSlug: string
    promocode?: string | null
  }) => {
    try {
      const url = isUserless
        ? api.orders.userless.createOrder
        : api.orders.createOrder
      const res = await url({
        data: {
          productSlug,
          billingAddressId,
          promocode,
        },
      })
      const order = "order" in res ? res.order : res

      if (order.paymentPlans.length === 0) {
        throw new Error("No payment plans found")
      }
      setOrder(order)
      return res
    } catch (error) {
      mapErrorsV2(error)
    }
  }

  return {
    createOrder,
    checkout: async (options?: {
      paymentPlanId?: string | null
      userlessOrderToken?: string | null
      promocode?: string | null
    }) => {
      const id = options?.paymentPlanId || paymentPlanId
      if (!id) throw new Error("Payment plan ID not found")

      await checkout(id, options)
    },
    updateSubscriptionPaymentMethod,
    stripePaymentElement: stripeForm,
    changePaymentPlan,
    orderId: order?.id,
  }
}

const getStripeSuccessRedirectURL = (redirectRoute: string) => {
  return new URL(redirectRoute, window.location.origin).toString()
}
